import { useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import "../../App.css";
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { Row, Col, Button } from "reactstrap";
import React from 'react';
import { default as ReactSelect } from "react-select";
import { components } from "react-select";
import { getAllPlants } from '../../services/PlantServices';
import { getAllProducts } from "../../services/ProductServices";
import { getAllVendors } from "../../services/VendorServices";
import { getGoodsReceiptNote } from "../../services/ReportServices";
import { getAllEquipments } from '../../services/EquipmentServices';
import { DatePicker } from 'antd';
import moment from 'moment';
import { displayError } from '../../helpers';
import ReportExportDropdown from '../../components/ReportExportDropdown';

const { RangePicker } = DatePicker;

function GoodsReceiptNoteTable() {
    const InitialData = {
        total_pages: 0,
        records: [],
    };
    const [data, setData] = useState(InitialData);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(false);

    const [plants, setPlants] = React.useState([]);
    const [plantAliasById, setPlantAliasById] = useState({});
    const [selectedPlant, setSelectedPlant] = useState([]);
    const [vendors, setVendors] = React.useState([]);
    const [selectedVendor, setSelectedVendor] = useState([]);
    const [vehicles, setVehicles] = React.useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState([]);
    const [selectedGrade, setSelectedGrade] = useState([]);
    const [grades, setGrades] = useState([]);
    const [reportFormat, setReportFormat] = useState('datewise');

    const initialFromDate = moment().startOf('month');
    const initialToDate = moment();
    const [fromDate, setFromDate] = useState(initialFromDate);
    const [toDate, setToDate] = useState(initialToDate);

    const Option = (props) => (
        <components.Option {...props}>
            <input type="checkbox" checked={props.isSelected} onChange={() => null} />
            {' '}
            <label>{props.label}</label>
        </components.Option>
    );

    React.useEffect(() => {
        getAllPlants(cookies)
            .then((plantList) => {
                const plantOptions = (plantList?.plant_list || [])
                    .filter((obj) => obj.status)
                    .map((plant) => ({
                        value: plant.id,
                        label: plant.plant_alias || plant.entity_name,
                    }));
                setPlants(plantOptions);
                setPlantAliasById(
                    Object.fromEntries(
                        (plantList?.plant_list || []).map((plant) => [
                            plant.id,
                            plant.plant_alias || plant.entity_name,
                        ]),
                    ),
                );
            })
            .catch((error) => displayError(error?.response?.data || error));
    }, [cookies]);

    React.useEffect(() => {
        getAllVendors(cookies)
            .then((vendorList) => {
                const vendorOptions = (vendorList?.vendor_list || []).map((vendor) => ({
                    value: vendor.id,
                    label: vendor.entity_name,
                }));
                setVendors(vendorOptions);
            })
            .catch((error) => displayError(error?.response?.data || error));
    }, [cookies]);

    React.useEffect(() => {
        getAllEquipments(cookies)
            .then((equipmentObject) => {
                const equipmentOptions = (equipmentObject?.equipment_list || [])
                    .filter((obj) => obj.status)
                    .map((equipment) => ({ value: equipment.id, label: equipment.equip_name }));
                setVehicles(equipmentOptions);
            })
            .catch((error) => displayError(error?.response?.data || error));
    }, [cookies]);

    React.useEffect(() => {
        getAllProducts(cookies)
            .then((productList) => {
                const productOptions = (productList?.product_list || [])
                    .filter((obj) => obj.status)
                    .map((product) => ({ value: product.id, label: product.name }));
                setGrades(productOptions);
            })
            .catch((error) => displayError(error?.response?.data || error));
    }, [cookies]);

    const selectedIds = (selected) => (selected || []).map((item) => item.value);

    const parseReportDate = (value) => {
        const parsed = moment(value, 'DD-MM-YYYY', true);
        return parsed.isValid() ? parsed : null;
    };

    const filterRecords = (records) => {
        const plantIds = selectedIds(selectedPlant);
        const vendorIds = selectedIds(selectedVendor);
        const vehicleIds = selectedIds(selectedVehicle);
        const gradeIds = selectedIds(selectedGrade);

        return records.filter((row) => {
            if (plantIds.length && !plantIds.includes(row.plant_id)) {
                return false;
            }
            if (vendorIds.length && !vendorIds.includes(row.vendor_id)) {
                return false;
            }
            if (gradeIds.length && !gradeIds.includes(row.product_id)) {
                return false;
            }
            if (vehicleIds.length) {
                const selectedLabels = vehicles
                    .filter((vehicle) => vehicleIds.includes(vehicle.value))
                    .map((vehicle) => vehicle.label);
                const matchesVehicle = (row.vehicle_id && vehicleIds.includes(row.vehicle_id))
                    || selectedLabels.includes(row.vehicle_label);
                if (!matchesVehicle) {
                    return false;
                }
            }
            const rowDate = parseReportDate(row.goods_receipt_note_date);
            if (rowDate && (rowDate.isBefore(fromDate, 'day') || rowDate.isAfter(toDate, 'day'))) {
                return false;
            }
            return true;
        });
    };

    const sortRecords = (records) => {
        const sorted = [...records];
        if (reportFormat === 'product') {
            sorted.sort((a, b) => (a.dc_grade || '').localeCompare(b.dc_grade || ''));
        } else if (reportFormat === 'Supplier') {
            sorted.sort((a, b) => (a.vendor_name || '').localeCompare(b.vendor_name || ''));
        } else if (reportFormat === 'Vehicle') {
            sorted.sort((a, b) => (a.vehicle_label || '').localeCompare(b.vehicle_label || ''));
        } else {
            sorted.sort((a, b) => {
                const aDate = parseReportDate(a.goods_receipt_note_date);
                const bDate = parseReportDate(b.goods_receipt_note_date);
                return (aDate?.valueOf() || 0) - (bDate?.valueOf() || 0);
            });
        }
        return sorted;
    };

    const mapReportRows = (rows) => rows.map((grn, index) => {
        const plantId = grn.plant?.id;
        const plantAlias = grn.plant_alias
            || grn.plant?.alias_name
            || plantAliasById[plantId]
            || '';

        return {
        id: grn.id || index,
        plant_alias: plantAlias,
        plant_id: plantId,
        goods_receipt_note_date: grn.goods_receipt_note_date,
        goods_receipt_note_no: `${grn.prefix || ''}${grn.goods_receipt_note_no || ''}`,
        dc_no: grn.dc_no,
        batch_no: grn.sl_no || '',
        dc_grade: grn.product?.name || '',
        product_id: grn.product?.id,
        goods_receipt_note_time: grn.goods_receipt_note_time,
        vendor_name: grn.vendor?.name || '',
        vendor_id: grn.vendor?.id,
        vehicle_label: grn.transport_mode === 'own'
            ? (grn.vehicle?.name || '')
            : (grn.vendor_vehicle_no || ''),
        vehicle_id: grn.vehicle?.id,
        received_qty: grn.received_qty,
        };
    });

    const handleGenerate = () => {
        setIsLoading(true);
        const params = {};
        if (selectedPlant?.length === 1) {
            params.plant_id = selectedPlant[0].value;
        }

        getGoodsReceiptNote(cookies, params)
            .then((response) => {
                const rawRows = mapReportRows(response?.goods_receipt_note_list || []);
                const filteredRows = sortRecords(filterRecords(rawRows));
                setData({ records: filteredRows });
                setIsLoading(false);
            })
            .catch((error) => {
                displayError(error?.response?.data || error);
                setData({ records: [] });
                setIsLoading(false);
            });
    };

    const columns = useMemo(
        () => [
            { fieldName: 'plant_alias', headerName: 'Plant' },
            { fieldName: 'goods_receipt_note_date', headerName: 'Date' },
            { fieldName: 'goods_receipt_note_no', headerName: 'Goods Receipt No' },
            { fieldName: 'dc_no', headerName: 'DC Number' },
            { fieldName: 'batch_no', headerName: 'Batch Number' },
            { fieldName: 'dc_grade', headerName: 'Grade' },
            { fieldName: 'goods_receipt_note_time', headerName: 'Time' },
            { fieldName: 'received_qty', headerName: 'Received Qty' },
        ],
        [],
    );

    const handleCSVExport = () => {
        if (!data.records.length) {
            Swal.fire('Generate the report first, then export.', '', 'info');
            return;
        }
        exportAsCSV(
            columns.map((c) => c.headerName),
            data.records.map((row) => ({
                plant_alias: row.plant_alias,
                goods_receipt_note_date: row.goods_receipt_note_date,
                goods_receipt_note_no: row.goods_receipt_note_no,
                dc_no: row.dc_no,
                batch_no: row.batch_no,
                dc_grade: row.dc_grade,
                goods_receipt_note_time: row.goods_receipt_note_time,
                received_qty: row.received_qty,
            })),
        );
    };

    const handlePDFExport = () => {
        if (!data.records.length) {
            Swal.fire('Generate the report first, then export.', '', 'info');
            return;
        }
        exportAsPDF(
            'Goods Receipt Note',
            columns.map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records,
            'GoodsReceipt_note.pdf',
        );
    };

    return (
        <div id="outer-container" className="App">
            <div id="page-wrap">
                <form className="form" onSubmit={(event) => { event.preventDefault(); handleGenerate(); }}>
                    <div className="container">
                        <Row>
                            <Col xl={12} lg={12} md={12}>
                                <div className="report-legacy-filters form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                    <ReactSelect
                                        options={plants}
                                        isMulti
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option }}
                                        isClearable
                                        value={selectedPlant}
                                        onChange={setSelectedPlant}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please select Plant name"
                                    />
                                    <br /><br />

                                    <ReactSelect
                                        options={vendors}
                                        isMulti
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option }}
                                        isClearable
                                        value={selectedVendor}
                                        onChange={setSelectedVendor}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please select Vendor"
                                    />
                                    <br /><br />

                                    <ReactSelect
                                        options={vehicles}
                                        isMulti
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option }}
                                        isClearable
                                        value={selectedVehicle}
                                        onChange={setSelectedVehicle}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please select Vehicle No"
                                    />
                                    <br /><br />

                                    <ReactSelect
                                        options={grades}
                                        isMulti
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option }}
                                        isClearable
                                        value={selectedGrade}
                                        onChange={setSelectedGrade}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please Select Grade"
                                    />

                                    <div className="report-legacy-filters-spacer" aria-hidden="true" />
                                    <div className="report-legacy-filters-spacer" aria-hidden="true" />

                                    <div className="report-legacy-filters-row">
                                        <RangePicker
                                            defaultValue={[initialFromDate, initialToDate]}
                                            format="DD-MM-YYYY"
                                            onChange={(values) => {
                                                if (values?.[0] && values?.[1]) {
                                                    setFromDate(values[0]);
                                                    setToDate(values[1]);
                                                }
                                            }}
                                            style={{ height: '40px', width: '270px' }}
                                        />

                                        <select
                                            className="browser-default custom-select col-sm-3"
                                            value={reportFormat}
                                            onChange={(event) => setReportFormat(event.target.value)}
                                            style={{ width: '200px' }}
                                        >
                                            <option value="datewise">Datewise</option>
                                            <option value="product">Product</option>
                                            <option value="Supplier">Supplier</option>
                                            <option value="Vehicle">Vehicle</option>
                                        </select>

                                        <div className="report-legacy-actions">
                                            <ReportExportDropdown
                                                onCSV={handleCSVExport}
                                                onPDF={handlePDFExport}
                                                disabled={isLoading || !data.records.length}
                                            />
                                            <Button
                                                color="primary"
                                                className="list-page-toolbar-generate-btn"
                                                type="button"
                                                onClick={handleGenerate}
                                                disabled={isLoading}
                                            >
                                                Generate
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </form>
                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="Goods Receipt Note"
                            columns={columns}
                            data={data.records}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            enableTableSearch={false}
                            isLoading={isLoading}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GoodsReceiptNoteTable;
